import React, { useMemo, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { Close, BookmarkRemove } from "@mui/icons-material";
import { List, type RowComponentProps } from "react-window";
import { useCart } from "../hooks/useCart";
import { useTranslation } from "react-i18next";
import { logger } from "@/shared/lib";
import { formatMoney } from "@/types/cart";
import { usePayment } from "@/features/payment";
import PaymentModal from "@/components/Payment/PaymentModal";

/**
 * Row model mixing headers + cart items + saved-for-later inside one virtualized list.
 */
type Row =
  | { kind: "header" }
  | { kind: "item"; index: number }
  | { kind: "savedHeader" }
  | { kind: "savedItem"; index: number };

type RowProps = {
  rows: Row[];
};

const CartPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { state, updateQty, removeItem, moveToCart, subtotalAmount } =
    useCart();
  const payment = usePayment();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const locale = i18n.language || "fr";
  const currency = state.items[0]?.price.currency || "EUR";

  const taxAmount = useMemo(() => {
    return state.items.reduce((sum, item) => {
      const rate =
        typeof item.taxRatePercent === "number" ? item.taxRatePercent : 0;
      if (!rate) return sum;
      const lineHt = item.price.amount * item.qty; // cents
      const lineTax = Math.round(lineHt * (rate / 100)); // cents
      return sum + lineTax;
    }, 0);
  }, [state.items]);

  // Build flattened row list (header -> items -> saved header -> saved items)
  const rows: Row[] = [];
  if (state.items.length > 0) {
    rows.push({ kind: "header" });
    for (let i = 0; i < state.items.length; i++)
      rows.push({ kind: "item", index: i });
  }
  if (state.savedForLater.length > 0) {
    rows.push({ kind: "savedHeader" });
    for (let i = 0; i < state.savedForLater.length; i++)
      rows.push({ kind: "savedItem", index: i });
  }

  // Size the middle (scroll) area so List gets exact height
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [listSize, setListSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const update = () =>
      setListSize({
        width: el.clientWidth,
        height: el.clientHeight,
      });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, []);

  const handleCheckout = () => {
    setPaymentModalOpen(true);
    payment.createPayment();
  };

  const handlePaymentSuccess = (cartId?: number) => {
    logger.warn("Payment successful!", cartId);
    setPaymentModalOpen(false);
    // Optionally redirect to history page
  };

  const handlePaymentFailure = () => {
    logger.warn("Payment failed!");
    setPaymentModalOpen(false);
  };

  /**
   * Dynamic row height (pixels) per row kind.
   * Keep these generous so rows never clip when chips wrap on small screens.
   */
  function rowHeight(index: number, { rows }: RowProps) {
    const r = rows[index];
    switch (r.kind) {
      case "header":
        return 56;
      case "item":
        return 124;
      case "savedHeader":
        return 56;
      case "savedItem":
        return 88;
      default:
        return 96;
    }
  }

  /**
   * Row renderer: receives index + injected RowProps + style from react-window v2.
   */
  function RowComponent({ index, style, rows }: RowComponentProps<RowProps>) {
    const r = rows[index];

    if (r.kind === "header") {
      return (
        <Box
          style={style}
          sx={{
            px: { xs: 1, sm: 2 },
            py: 1,
            color: "text.secondary",
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            display: "grid",
            gridTemplateColumns: {
              xs: "70px 1fr",
              sm: "90px 1fr",
              md: "100px 1fr",
              lg: "110px 1fr",
            },
          }}
        ></Box>
      );
    }

    if (r.kind === "savedHeader") {
      return (
        <Box
          style={style}
          sx={{ px: { xs: 1, sm: 2 }, display: "flex", alignItems: "center" }}
        >
          <Typography variant="h6">
            {t("cart.moveToCart", "Move to cart")}
          </Typography>
        </Box>
      );
    }

    if (r.kind === "savedItem") {
      const s = state.savedForLater[r.index];
      return (
        <ListItem
          style={style}
          alignItems="flex-start"
          secondaryAction={
            <IconButton
              aria-label={t("cart.moveToCart", "Move to cart")}
              onClick={() => moveToCart(s.id)}
            >
              <BookmarkRemove />
            </IconButton>
          }
        >
          <ListItemText
            primary={s.title}
            secondary={formatMoney(s.price, locale)}
          />
        </ListItem>
      );
    }

    // Regular cart item row
    const item = state.items[r.index];
    const rate = item.taxRatePercent as number | undefined;
    const ttc =
      typeof rate === "number" && rate > 0
        ? Math.round(item.price.amount * (1 + rate / 100))
        : undefined;
    const lineTotalHt = item.price.amount * item.qty;
    const lineTotalTax =
      typeof rate === "number" && rate > 0
        ? Math.round(lineTotalHt * (rate / 100))
        : 0;
    const lineTotalTtc = lineTotalHt + lineTotalTax;

    return (
      <ListItem
        style={style}
        alignItems="center"
        sx={{
          px: { xs: 1, sm: 2 },
          py: { xs: 1.5, sm: 2 },
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
        secondaryAction={
          <IconButton
            aria-label={`${t("cart.remove", "Remove")} ${item.title}`}
            onClick={() => removeItem(item.id)}
          >
            <Close />
          </IconButton>
        }
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "70px 1fr",
              sm: "90px 1fr",
              md: "100px 1fr",
              lg: "110px 1fr",
            },
            alignItems: "center",
            width: "100%",
            gap: { xs: 0.5, sm: 2 },
          }}
        >
          {/* Qty first column */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TextField
              size="small"
              value={item.qty}
              type="number"
              inputProps={{
                min: 1,
                max: item.maxQty || undefined,
                "aria-label": t("cart.qty", "Quantity"),
              }}
              onChange={(e) => {
                const next = Math.max(
                  1,
                  Math.floor(Number(e.target.value) || 1)
                );
                updateQty(
                  item.id,
                  item.maxQty ? Math.min(next, item.maxQty) : next
                );
              }}
              sx={{
                width: { xs: 60, sm: 80 },
                "& .MuiInputBase-input": {
                  textAlign: "center",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                },
              }}
            />
          </Box>

          {/* Product info column with details below title */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 2 },
              }}
            >
              {item.thumbnail && (
                <img
                  src={item.thumbnail}
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: "cover",
                    borderRadius: 8,
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                />
              )}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {item.title}
                </Typography>
                <Box
                  sx={{ display: "flex", gap: 2, mt: 0.5, flexWrap: "wrap" }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t("cart.ht", "Excl. VAT")}:{" "}
                    {formatMoney(
                      {
                        amount: item.price.amount,
                        currency: item.price.currency,
                      },
                      locale
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("cart.tax", "VAT")}:{" "}
                    {typeof rate === "number" && rate > 0
                      ? `${rate.toFixed(2)}%`
                      : "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("cart.ttc", "Incl. VAT")}:{" "}
                    {formatMoney(
                      {
                        amount:
                          typeof ttc === "number" ? ttc : item.price.amount,
                        currency: item.price.currency,
                      },
                      locale
                    )}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
                  {t("cart.total", "Total")}:{" "}
                  {formatMoney(
                    { amount: lineTotalTtc, currency: item.price.currency },
                    locale
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </ListItem>
    );
  }

  return (
    <Box
      role="main"
      sx={{
        display: "grid",
        gridTemplateRows: "auto 1fr auto", // header, list, footer
        height: "100%", // inherit from parent layout container
        maxHeight: "100%",
        minHeight: 0,
        overflow: "hidden", // prevent body scroll
        overscrollBehavior: "contain",
      }}
    >
      {/* Header (non-scrolling) */}
      <Box sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 }, pb: 1 }}>
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("cart.title", "Your cart")}
        </Typography>
      </Box>

      {/* Scrollable content — virtualization handles scroll */}
      <Box
        ref={scrollContainerRef}
        sx={{
          minHeight: 0,
          overflow: "hidden", // List owns the scroll
          px: 0,
        }}
      >
        {state.items.length === 0 ? (
          <Card sx={{ mx: { xs: 1, sm: 2 } }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                {t("cart.empty", "Your cart is empty")}
              </Typography>
              <Button
                variant="text"
                href="/concierge"
                sx={{
                  mt: 1,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              >
                {t("cart.continueShopping", "Continue shopping")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <List<RowProps>
            style={{
              height: listSize.height,
              width: listSize.width || "100%",
            }}
            rowComponent={RowComponent}
            rowCount={rows.length}
            rowHeight={rowHeight}
            rowProps={{ rows }}
          />
        )}
      </Box>

      {/* Footer (always visible, visually matches the Card) */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 1,
          bgcolor: "transparent", // no background band
          borderTop: "none",
          p: { xs: 1, sm: 2 },
          flexShrink: 0,
          pb: `calc(env(safe-area-inset-bottom))`,
        }}
      >
        <Card>
          <CardContent>
            {state.items.some((i) => typeof i.taxRatePercent === "number") && (
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  {t("cart.tax", "VAT")}
                </Typography>
                <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                  {formatMoney({ amount: taxAmount, currency }, locale)}
                </Typography>
              </Box>
            )}

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                {t("cart.subtotal", "Subtotal")}
              </Typography>
              <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                {formatMoney(subtotalAmount, locale)}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography
                fontWeight={600}
                sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}
              >
                {t("cart.totalTtc", "Total TTC")}
              </Typography>
              <Typography
                fontWeight={600}
                sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }}
              >
                {formatMoney(
                  {
                    amount: subtotalAmount.amount + taxAmount,
                    currency: subtotalAmount.currency || currency,
                  },
                  locale
                )}
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleCheckout}
              disabled={state.items.length === 0}
              sx={{
                fontSize: { xs: "0.875rem", sm: "1rem" },
                py: { xs: 1.5, sm: 2 },
              }}
            >
              {t("cart.checkout", "Checkout")}
            </Button>
          </CardContent>
        </Card>
      </Box>

      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onSuccess={handlePaymentSuccess}
        onFailure={handlePaymentFailure}
        variant="cart"
        paymentStatus={payment.paymentStatus}
        onConfirmPayment={payment.confirmPayment}
        onDeleteCart={payment.deleteCart}
        onResetPayment={payment.resetPayment}
        onCheckPaymentStatus={payment.checkPaymentStatus}
      />
    </Box>
  );
};

export default CartPage;
